import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, Timestamp, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';

interface NotificationContextType {
  showNotification: (message: string, type: 'success' | 'error' | 'info') => void;
  unreadReservations: number;
  clearReservationNotifications: () => void;
  resetNotificationsOnLogin: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Key for storing the last login time (when notifications were reset)
const LAST_LOGIN_KEY = 'lastLoginTime';

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [unreadReservations, setUnreadReservations] = useState<number>(0);
  
  const showNotification = (message: string, type: 'success' | 'error' | 'info') => {
    // For now, just log to console
    console.log(`[${type.toUpperCase()}] ${message}`);
  };
  
  // Clear reservation notifications (manual clear)
  const clearReservationNotifications = () => {
    setUnreadReservations(0);
    localStorage.setItem(LAST_LOGIN_KEY, new Date().toISOString());
  };
  
  // Reset notifications on login
  const resetNotificationsOnLogin = () => {
    setUnreadReservations(0);
    localStorage.setItem(LAST_LOGIN_KEY, new Date().toISOString());
    console.log('Notifications reset on login');
  };
  
  // Listen for new reservations since last login
  useEffect(() => {
    // Get the last login time
    const lastLoginStr = localStorage.getItem(LAST_LOGIN_KEY);
    const lastLogin = lastLoginStr ? new Date(lastLoginStr) : new Date(0); // Default to epoch if not set
    
    console.log('Setting up notification listener since last login:', lastLogin.toISOString());
    
    // Create a Firestore timestamp from lastLogin date
    const lastLoginTimestamp = Timestamp.fromDate(lastLogin);
    
    // Query for reservations created after the last login
    const reservationsRef = collection(db, 'reservations');
    const newReservationsQuery = query(
      reservationsRef,
      where('createdAt', '>', lastLoginTimestamp),
      orderBy('createdAt', 'desc')
    );
    
    // Subscribe to query
    const unsubscribe = onSnapshot(newReservationsQuery, (snapshot) => {
      const newCount = snapshot.docs.length;
      console.log(`Found ${newCount} new reservations since last login`);
      setUnreadReservations(newCount);
    }, (error) => {
      console.error('Error fetching new reservations:', error);
    });
    
    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);
  
  return (
    <NotificationContext.Provider value={{ 
      showNotification, 
      unreadReservations,
      clearReservationNotifications,
      resetNotificationsOnLogin
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}; 